/**
 * Sidebar Mutation Events
 *
 * Browser-only bridge for local chat and Space mutations.
 *
 * Primary responsibilities:
 * - Emit sidebar updates after successful create/update helpers
 * - Emit sidebar read-state clears after a chat is seen
 * - Subscribe the mounted sidebar to same-tab mutation results
 *
 * Dependencies:
 * - Browser CustomEvent API
 */

import type { Tables } from '@/supabase/types'

export const SIDEBAR_MUTATION_EVENT = 'sidebar-mutation'

type SidebarMutationAction = 'created' | 'updated' | 'deleted'

type SidebarChatMutationDetail = {
  action: 'created' | 'updated'
  chat: Tables<'chats'>
  target: 'chat'
}

type SidebarChatDeletedDetail = {
  action: 'deleted'
  chatId: string
  target: 'chat'
}

type SidebarChatUnreadActivityClearedDetail = {
  action: 'unread_activity_cleared'
  chatId: string
  target: 'chat'
}

type SidebarSpaceMutationDetail = {
  action: SidebarMutationAction
  space: Tables<'spaces'>
  target: 'space'
}

export type SidebarMutationDetail =
  | SidebarChatMutationDetail
  | SidebarChatDeletedDetail
  | SidebarChatUnreadActivityClearedDetail
  | SidebarSpaceMutationDetail

const canUseWindow = () => typeof window !== 'undefined'

const emitSidebarMutation = (detail: SidebarMutationDetail) => {
  if (!canUseWindow()) {
    return
  }

  window.dispatchEvent(
    new CustomEvent<SidebarMutationDetail>(SIDEBAR_MUTATION_EVENT, {
      detail
    })
  )
}

export const notifySidebarChatCreated = (chat: Tables<'chats'>) => {
  emitSidebarMutation({
    action: 'created',
    chat,
    target: 'chat'
  })
}

export const notifySidebarChatUpdated = (chat: Tables<'chats'>) => {
  emitSidebarMutation({
    action: 'updated',
    chat,
    target: 'chat'
  })
}

export const notifySidebarChatDeleted = (chatId: string) => {
  const id = chatId.trim()
  if (!id) return
  emitSidebarMutation({
    action: 'deleted',
    chatId: id,
    target: 'chat'
  })
}

export const notifySidebarChatUnreadActivityCleared = (chatId: string) => {
  emitSidebarMutation({
    action: 'unread_activity_cleared',
    chatId,
    target: 'chat'
  })
}

export const notifySidebarSpaceCreated = (space: Tables<'spaces'>) => {
  emitSidebarMutation({
    action: 'created',
    space,
    target: 'space'
  })
}

export const notifySidebarSpaceUpdated = (space: Tables<'spaces'>) => {
  emitSidebarMutation({
    action: 'updated',
    space,
    target: 'space'
  })
}

export const subscribeToSidebarMutations = (
  onMutation: (detail: SidebarMutationDetail) => void
) => {
  if (!canUseWindow()) {
    return () => {}
  }

  const handleMutation = (event: Event) => {
    const detail = (event as CustomEvent<SidebarMutationDetail>).detail

    if (!detail) {
      return
    }

    onMutation(detail)
  }

  window.addEventListener(SIDEBAR_MUTATION_EVENT, handleMutation)

  return () => {
    window.removeEventListener(SIDEBAR_MUTATION_EVENT, handleMutation)
  }
}
